import React, { useMemo, useState } from "react";
import BdrModal from "../components/BdrModal/BdrModal";
import BdrButton from "../components/BdrButton/BdrButton";
import { canStartHere, itemsForPage, keepsNew, newItems } from "./tourEngine";

/**
 * What's New + Help, one modal with three tabs.
 *
 *   What's new  unseen tours, each Start or Dismiss, plus Dismiss all.
 *               Seen = started OR dismissed (owner D3). An ASSIGNED tour has
 *               no Dismiss: it is a to-do that stays here until completed.
 *   This page   every tour for the page the user is on — replayable.
 *   All         every tour the user can see.
 *
 * mode "popup" is the one-time announcement on the first load after a release
 * (owner D1): same modal, opens on What's new with a friendlier title. It never
 * starts a tour by itself.
 */

const fmtDate = (ms) => {
  const n = Number(ms);
  if (!Number.isFinite(n)) return "";
  return new Date(n).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function TourRow({ item, registry, pathname, onStart, onDismiss, showDismiss }) {
  const startable = canStartHere(item, registry, pathname);
  const steps = (registry[item.id]?.steps || []).length;
  const due = keepsNew(item);
  return (
    <li className="bdr-tour-hub-row">
      <div className="bdr-tour-hub-row-text">
        <div className="bdr-tour-hub-row-title">
          {item.title}
          {item.isPreview && <span className="bdr-badge bdr-badge--warning">Preview</span>}
          {due && <span className="bdr-badge">Assigned</span>}
          {item.completedAt && <span className="bdr-badge bdr-badge--accent">Done</span>}
        </div>
        {item.summary && <p className="bdr-tour-hub-row-summary">{item.summary}</p>}
        <div className="bdr-tour-hub-row-meta">
          {fmtDate(item.releasedAt)} · {steps} step{steps === 1 ? "" : "s"}
          {!startable && item.startMode === "here-only" && " · open the page it covers to take it"}
          {due && item.isNew && " · assigned to you — complete it to clear it"}
        </div>
      </div>
      <div className="bdr-tour-hub-row-actions">
        {showDismiss && !due && (
          <BdrButton size="sm" onClick={() => onDismiss([item.id])}>
            Dismiss
          </BdrButton>
        )}
        <BdrButton size="sm" variant="action" disabled={!startable} onClick={() => onStart(item.id)}>
          {item.completedAt ? "Replay" : "Start"}
        </BdrButton>
      </div>
    </li>
  );
}

const TourHub = ({ mode, items, registry, pathname, onStart, onDismiss, onClose }) => {
  const fresh = useMemo(() => newItems(items), [items]);
  const dismissable = useMemo(() => fresh.filter((it) => !keepsNew(it)), [fresh]);
  const here = useMemo(() => itemsForPage(items, registry, pathname), [items, registry, pathname]);
  const initialTab = mode === "popup" || mode === "new" ? "new" : here.length ? "page" : "all";
  const [tab, setTab] = useState(fresh.length || initialTab !== "new" ? initialTab : "all");

  const tabs = [
    { key: "new", label: `What's new${fresh.length ? ` (${fresh.length})` : ""}` },
    { key: "page", label: "This page" },
    { key: "all", label: "All" },
  ];
  const list = tab === "new" ? fresh : tab === "page" ? here : items;
  const empty =
    tab === "new"
      ? "You're all caught up."
      : tab === "page"
        ? "No walkthroughs for this page yet."
        : "No walkthroughs yet.";

  return (
    <BdrModal
      open
      onClose={onClose}
      title={mode === "popup" ? "New since your last visit" : "Help & walkthroughs"}
      size="md"
      actions={
        <>
          {tab === "new" && dismissable.length > 1 && (
            <BdrButton onClick={() => onDismiss(dismissable.map((it) => it.id))}>Dismiss all</BdrButton>
          )}
          <BdrButton onClick={onClose}>Close</BdrButton>
        </>
      }
    >
      {/* Tabs change the view, so the selected one takes the navigational
          "action" intent (style guide §6b) — no local toggle recipe. */}
      <div className="bdr-tour-hub-tabs" role="group" aria-label="Show walkthroughs">
        {tabs.map((t) => (
          <BdrButton
            key={t.key}
            size="sm"
            variant={tab === t.key ? "action" : "plain"}
            aria-pressed={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </BdrButton>
        ))}
      </div>
      {list.length === 0 ? (
        <p className="bdr-empty">{empty}</p>
      ) : (
        <ul className="bdr-tour-hub-list">
          {list.map((item) => (
            <TourRow
              key={item.id}
              item={item}
              registry={registry}
              pathname={pathname}
              onStart={onStart}
              onDismiss={onDismiss}
              showDismiss={tab === "new"}
            />
          ))}
        </ul>
      )}
    </BdrModal>
  );
};

export default TourHub;
